/**
 * Online Payment Model
 * Represents an online payment record from Firestore
 */


export class OnlinePayment {
    constructor({
        Class,
        academicYear = '',
        amount = 0.0,
        name = '',
        orderId = '',
        paymentId = '',
        sec = '',
        signature = '',
        time = null,
        userId = '',
        id = null,
        paymentStatus = null
    } = {}) {
        this.Class = Class;
        this.academicYear = academicYear;
        this.amount = parseFloat(amount) || 0.0;
        this.name = name;
        this.orderId = orderId;
        this.paymentId = paymentId;
        this.sec = sec;
        this.signature = signature;
        this.time = time; // Will be converted to JS Date in fromMap
        this.userId = userId;
        this.id = id;
        this.paymentStatus = paymentStatus;
    }

    /**
     * Creates an OnlinePayment instance from Firestore document data
     * @param {Object} data - Firestore document data
     * @param {string} docId - Document ID
     * @returns {OnlinePayment} OnlinePayment instance
     */
    static fromMap(data, docId = null) {
        if (!data) {
            return new OnlinePayment();
        }

        // Convert Firestore Timestamp to JavaScript Date
        let timeValue = null;
        if (data.time) {
            // Check if it's a Firestore Timestamp
            if (data.time.toDate && typeof data.time.toDate === 'function') {
                timeValue = data.time.toDate();
            } else if (data.time instanceof Date) {
                timeValue = data.time;
            } else if (typeof data.time === 'string') {
                timeValue = new Date(data.time);
            }
        }

        return new OnlinePayment({
            Class: data.Class,
            academicYear: data.academicYear,
            amount: data.amount,
            name: data.name,
            orderId: data.orderId,
            paymentId: data.paymentId,
            sec: data.sec,
            signature: data.signature,
            time: timeValue,
            userId: data.userId,
            id: docId,
            paymentStatus: data.paymentStatus
        });
    }

    /**
     * Converts the OnlinePayment instance to a plain object for Firestore
     * @returns {Object} Plain object representation
     */
    toMap() {
        return {
            Class: this.Class,
            academicYear: this.academicYear,
            amount: this.amount,
            name: this.name,
            orderId: this.orderId,
            paymentId: this.paymentId,
            sec: this.sec,
            signature: this.signature,
            time: this.time,
            userId: this.userId,
            paymentStatus: this.paymentStatus
        };
    }

    /**
     * Gets formatted amount with currency symbol
     * @returns {string} Formatted amount
     */
    getFormattedAmount() {
        return `₹${this.amount.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }

    /**
     * Gets formatted date string
     * @returns {string} Formatted date
     */
    getFormattedDate() {
        if (!this.time) return 'N/A';
        return this.time.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

}

export const PaymentStatus =  {
    AUTHORIZED: 'payment.authorized',
    FAILED: 'payment.failed',
    CAPTURED: 'payment.captured'
};

export default OnlinePayment;
